import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import { signIn } from "next-auth/react";

import { yupResolver } from "@hookform/resolvers/yup";
import PropTypes from "prop-types";

import { useRegister } from "@/api/client/auth";
import { useGetNotificationsData } from "@/api/client/notifications";
import PrimaryBtn from "@/components/actions/button/primaryBtn";
import AppInput from "@/components/data-input/input-field";
import { fbEvents, sendFPixelEvent } from "@/lib/fpixel";
import { newUserSchema } from "@/schema/formSchema";
import { removeGuestUser } from "@/services/guest-user";
import { updateCartItemCount } from "@/store/feature/cartSlice";
import { updateWishlistItemCount } from "@/store/feature/wishlistSlice";
import { authorizedRedirection } from "@/utils/helpers";

function NewUserForm({ prevData }) {
  const dispatch = useDispatch();

  const { refetch: refetchNotificaitonData } = useGetNotificationsData({
    enabled: false,
    onSuccess: (data) => {
      dispatch(updateCartItemCount(data.cartProductCount));
      dispatch(updateWishlistItemCount(data.wishlistProductCount));
    },
  });

  const { isLoading: isRegisterLoading, mutate: registerUser } = useRegister({
    onSuccess: async (registerData) => {
      const user = {
        token: registerData?.data?.token,
        name: registerData?.data?.customer?.full_name,
        email: registerData?.data?.customer?.email_address,
        phone: registerData?.data?.customer?.phone_number,
      };

      const res = await signIn("credentials", { redirect: false, ...user });
      if (res) {
        authorizedRedirection();
        removeGuestUser();
        refetchNotificaitonData();
      }
      sendFPixelEvent(fbEvents.completeRegistration);
    },
    onError: (registerError) => {
      registerError?.errors.map(({ name, message }) => {
        setError(name, { message });
      });
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: yupResolver(newUserSchema),
  });

  const handleRegisterUser = (newUserData) => {
    let registerPayload = {
      full_name: newUserData.full_name,
      email: newUserData.email,
      phone: prevData.phone,
      reg_signature: prevData.signature,
    };
    registerUser(registerPayload);
  };

  return (
    <form className="w-full" onSubmit={handleSubmit(handleRegisterUser)}>
      <h1 className="mb-[2.25rem] text-[1.25rem] font-semibold tracking-[0.14px] text-grayDisplay-700 md:mb-[2.75rem] md:text-[1.75rem]">
        Please fill the Information
      </h1>
      <div className="space-y-[1.5rem]">
        <AppInput
          labelName="Full Name"
          isFieldRequired
          placeholder="Enter full name"
          name="full_name"
          register={register}
          error={errors.full_name}
          className="w-full sm:max-w-[30rem]"
        />

        <AppInput
          labelName="Email Address"
          isFieldRequired
          placeholder="Enter email address"
          name="email"
          register={register}
          error={errors.email}
          className="w-full sm:max-w-[30rem]"
        />
      </div>

      <PrimaryBtn
        isLoading={isRegisterLoading}
        btnText="Submit"
        className="mt-[1.875rem] h-[3.5rem] w-full sm:max-w-[30rem]"
      />
    </form>
  );
}

NewUserForm.propTypes = {
  prevData: PropTypes.shape({
    phone: PropTypes.string,
    signature: PropTypes.string,
  }),
};

export default NewUserForm;
